import { useEffect, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import type { Location, Playlist, Schedule, Screen, ScreenGroup } from '@ghostscreen/contracts';
import { ConfirmDialog } from '../../components/dashboard/ConfirmDialog';
import { apiErrorMessage } from '../../i18n';
import { api } from '../../lib/api';
import { useTheme } from '../../theme/ThemeProvider';
import {
  emptyScheduleForm,
  ScheduleFormFields,
  scheduleToFormValues,
  type ScheduleFormValues,
} from './ScheduleFormFields';

type DaypartDrawerProps = {
  open: boolean;
  organisationId: string;
  schedule: Schedule | null;
  screens: Screen[];
  groups: ScreenGroup[];
  playlists: Playlist[];
  locations: Location[];
  initialScreenId?: string | null;
  initialScreenGroupId?: string | null;
  onClose: () => void;
  onSaved: () => void;
};

/**
 * New / edit daypart side panel — Ghost Screen Schedule.dc.html.
 * Portaled to body with theme + min-h-0 flex so footer stays pinned.
 */
export function DaypartDrawer({
  open,
  organisationId,
  schedule,
  screens,
  groups,
  playlists,
  locations,
  initialScreenId,
  initialScreenGroupId,
  onClose,
  onSaved,
}: DaypartDrawerProps) {
  const { t } = useTranslation(['schedule', 'common']);
  const { theme } = useTheme();
  const editing = Boolean(schedule);
  const [values, setValues] = useState<ScheduleFormValues>(() =>
    schedule
      ? scheduleToFormValues(schedule)
      : initialValues(screens, groups, initialScreenId, initialScreenGroupId),
  );
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!open) return;
    setValues(
      schedule
        ? scheduleToFormValues(schedule)
        : initialValues(screens, groups, initialScreenId, initialScreenGroupId),
    );
    setError(null);
    setConfirmDelete(false);
  }, [open, schedule, screens, groups, initialScreenId, initialScreenGroupId]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  const create = useMutation({
    mutationFn: () =>
      api.createSchedule(organisationId, {
        ...(values.name.trim() ? { name: values.name.trim() } : {}),
        ...(values.targetKind === 'screen'
          ? { screenId: values.screenId }
          : { screenGroupId: values.screenGroupId }),
        playlistId: values.playlistId,
        startLocalTime: values.startLocalTime,
        endLocalTime: values.endLocalTime,
        daysOfWeek: values.daysOfWeek,
        priority: Number(values.priority) || 0,
      }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: (err) =>
      setError(apiErrorMessage(err, t('drawer.createError'))),
  });

  const save = useMutation({
    mutationFn: () => {
      if (!schedule) throw new Error('Missing schedule');
      return api.updateSchedule(organisationId, schedule.id, {
        name: values.name.trim() ? values.name.trim() : null,
        playlistId: values.playlistId,
        startLocalTime: values.startLocalTime,
        endLocalTime: values.endLocalTime,
        daysOfWeek: values.daysOfWeek,
        priority: Number(values.priority) || 0,
      });
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: (err) =>
      setError(apiErrorMessage(err, t('drawer.saveError'))),
  });

  const remove = useMutation({
    mutationFn: () => {
      if (!schedule) throw new Error('Missing schedule');
      return api.deleteSchedule(organisationId, schedule.id);
    },
    onSuccess: () => {
      setConfirmDelete(false);
      onSaved();
      onClose();
    },
    onError: (err) =>
      setError(apiErrorMessage(err, t('drawer.deleteError'))),
  });

  if (!open) return null;

  const busy = create.isPending || save.isPending || remove.isPending;
  const targetReady =
    values.targetKind === 'screen' ? Boolean(values.screenId) : Boolean(values.screenGroupId);
  const canSubmit =
    targetReady &&
    Boolean(values.playlistId) &&
    values.startLocalTime !== values.endLocalTime &&
    !busy;
  const formReady = playlists.length > 0 && (screens.length > 0 || groups.length > 0);

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[100]"
        style={{ background: 'rgba(4,4,6,0.58)' }}
        onClick={onClose}
        aria-hidden
      />

      <aside
        data-theme={theme}
        role="dialog"
        aria-modal="true"
        aria-labelledby="daypart-drawer-title"
        className="fixed bottom-0 end-0 top-0 z-[101] flex w-[min(460px,100vw)] flex-col border-s shadow-[-24px_0_60px_rgba(0,0,0,0.35)] rtl:shadow-[24px_0_60px_rgba(0,0,0,0.35)]"
        style={{
          background: 'var(--gs-panel)',
          color: 'var(--gs-ink)',
          borderColor: 'var(--gs-line)',
          animation: 'gs-slide 220ms ease both',
          fontFamily: 'inherit',
        }}
      >
        <div
          className="flex shrink-0 items-start gap-3.5 border-b px-6 py-5"
          style={{ borderColor: 'var(--gs-line)' }}
        >
          <span className="flex min-w-0 flex-1 flex-col gap-1">
            <span
              id="daypart-drawer-title"
              className="text-[18px] font-extrabold tracking-[-0.02em]"
              style={{ color: 'var(--gs-ink)' }}
            >
              {editing ? t('drawer.editTitle') : t('drawer.newTitle')}
            </span>
            <span
              className="text-[13px]"
              style={{ color: 'var(--gs-muted)', lineHeight: 1.45 }}
            >
              {t('drawer.subtitle')}
            </span>
          </span>
          <button
            type="button"
            title={t('common:actions.close')}
            onClick={onClose}
            className="grid shrink-0 place-items-center p-[5px]"
            style={{ color: 'var(--gs-muted)', background: 'none', border: 'none' }}
          >
            <svg
              width="19"
              height="19"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden
            >
              <path d="m6 6 12 12M18 6 6 18" />
            </svg>
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-[22px]">
          {!formReady ? (
            playlists.length === 0 ? (
              <p className="m-0 text-sm" style={{ color: 'var(--gs-muted)' }}>
                <Trans
                  t={t}
                  i18nKey="drawer.needPlaylist"
                  components={{
                    playlistsLink: (
                      <Link
                        to="/playlists"
                        className="font-semibold"
                        style={{ color: 'var(--gs-accent)' }}
                        onClick={onClose}
                      />
                    ),
                  }}
                />
              </p>
            ) : (
              <p className="m-0 text-sm" style={{ color: 'var(--gs-muted)' }}>
                <Trans
                  t={t}
                  i18nKey="drawer.needTarget"
                  components={{
                    groupsLink: (
                      <Link
                        to="/groups"
                        className="font-semibold"
                        style={{ color: 'var(--gs-accent)' }}
                        onClick={onClose}
                      />
                    ),
                  }}
                />
              </p>
            )
          ) : (
            <form
              id="daypart-drawer-form"
              onSubmit={(event: FormEvent) => {
                event.preventDefault();
                setError(null);
                if (editing) save.mutate();
                else create.mutate();
              }}
            >
              <ScheduleFormFields
                values={values}
                onChange={setValues}
                screens={screens}
                groups={groups}
                playlists={playlists}
                locations={locations}
                disabled={busy}
                targetLocked={editing}
                showPreview
              />
              {error ? (
                <p className="m-0 mt-[22px] text-sm" style={{ color: 'var(--gs-bad)' }} role="alert">
                  {error}
                </p>
              ) : null}
            </form>
          )}
        </div>

        <div
          className="flex shrink-0 flex-wrap items-center gap-2.5 border-t px-4 py-4 sm:px-6"
          style={{ borderColor: 'var(--gs-line)' }}
        >
          {editing ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => setConfirmDelete(true)}
              className="rounded-[10px] border px-4 py-3 text-sm font-semibold disabled:opacity-50"
              style={{
                color: 'var(--gs-bad)',
                borderColor: 'var(--gs-line)',
                background: 'none',
                fontFamily: 'inherit',
              }}
            >
              {t('common:actions.delete')}
            </button>
          ) : null}

          <button
            type="button"
            onClick={onClose}
            className="rounded-[10px] border px-4 py-3 text-sm font-semibold"
            style={{
              color: 'var(--gs-ink2)',
              background: 'var(--gs-panel2)',
              borderColor: 'var(--gs-line)',
              fontFamily: 'inherit',
            }}
          >
            {t('common:actions.cancel')}
          </button>

          {formReady ? (
            <button
              type="submit"
              form="daypart-drawer-form"
              disabled={!canSubmit}
              className="min-w-0 flex-[1_1_100%] rounded-[10px] border-none px-[18px] py-[13px] text-[14.5px] font-bold disabled:cursor-not-allowed sm:flex-[1_1_auto]"
              style={{
                color: 'var(--gs-accent-ink)',
                background: 'var(--gs-accent-solid)',
                opacity: canSubmit ? 1 : 0.45,
                fontFamily: 'inherit',
              }}
            >
              {busy
                ? editing
                  ? t('common:actions.saving')
                  : t('drawer.creating')
                : editing
                  ? t('drawer.saveChanges')
                  : t('drawer.create')}
            </button>
          ) : null}
        </div>
      </aside>

      {confirmDelete && schedule ? (
        <ConfirmDialog
          title={
            schedule.name?.trim()
              ? t('drawer.confirmDeleteTitle', { name: schedule.name.trim() })
              : t('drawer.confirmDeleteTitleUnnamed')
          }
          description={t('drawer.confirmDeleteDescription')}
          confirmLabel={t('common:actions.delete')}
          tone="danger"
          confirming={remove.isPending}
          onCancel={() => {
            if (!remove.isPending) setConfirmDelete(false);
          }}
          onConfirm={() => remove.mutate()}
        />
      ) : null}
    </>,
    document.body,
  );
}

function initialValues(
  screens: Screen[],
  groups: ScreenGroup[],
  initialScreenId?: string | null,
  initialScreenGroupId?: string | null,
): ScheduleFormValues {
  const base = emptyScheduleForm();
  if (initialScreenGroupId) {
    return { ...base, targetKind: 'group', screenGroupId: initialScreenGroupId };
  }
  if (initialScreenId) {
    return { ...base, targetKind: 'screen', screenId: initialScreenId };
  }
  if (screens.length === 0 && groups.length > 0) {
    return { ...base, targetKind: 'group' };
  }
  return base;
}
